import {
  DOB_OAUTH_AUTHORITIES,
  DOB_OAUTH_AUTHORIZE_PATH,
  DOB_OAUTH_FORCE_VIEW,
  DOB_OAUTH_SCOPE,
  DOB_OAUTH_STATE_SCOPE,
} from '@/constants/auth'
import { WHITE_LIST } from '@/constants/domain'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { routing } from '@/i18n/routing'
import { getCreateDomain, getCurrentDomain, getViveAvatarDomain } from '@/lib/configs'
import { env } from '@/lib/runtime-env'
import { applyUnderline } from '@/lib/utils'
import { getUserLocale } from '@/server/services/locales'
import { API_ENPOINT_LOGIN, API_ENPOINT_LOGOUT } from '@/services/api'

function getUrlContext() {
  const domain = getCurrentDomain()
  return {
    whiteList: [domain, ...WHITE_LIST],
    mainPage: `https://${domain}`,
  }
}

function isBlockedUrl(hostname: string, pathname: string, whiteList: string[]) {
  // TODO need to handle errors page
  return pathname.includes('/errors') || !whiteList.includes(hostname)
}

export function getLocationHrefUrl(extraParams = {}) {
  const { whiteList, mainPage } = getUrlContext()

  if (isBlockedUrl(window.location.hostname, window.location.pathname, whiteList)) {
    return mainPage
  }

  const url = new URL(window.location.href)
  Object.entries(extraParams).forEach(([key, value]) => {
    url.searchParams.set(key, value as string)
  })
  return url.href
}

function getReferrerUrl() {
  const { whiteList, mainPage } = getUrlContext()

  if (!document.referrer) {
    return isBlockedUrl(window.location.hostname, window.location.pathname, whiteList)
      ? mainPage
      : window.location.href
  }

  const url = new URL(document.referrer)
  return isBlockedUrl(url.hostname, url.pathname, whiteList)
    ? mainPage
    : url.origin + url.pathname + url.search
}

export type LoginParams = {
  callback?: string
  loginParams?: Record<string, string>
  vodafoneURLPostfix?: string
}

type DobOAuthUrlParams = {
  locale: string
}

function getDobOAuthStateUrl() {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }

  return `https://${getCreateDomain()}`
}

export async function buildLoginBaseUrl() {
  const locale = applyUnderline(await getUserLocale())
  const domain = getCreateDomain()
  return `https://${domain}${API_ENPOINT_LOGIN}?hl=${locale}`
}

export function buildDobOAuthUrl({ locale }: DobOAuthUrlParams) {
  const accountServer = env('NEXT_PUBLIC_ACCOUNT_SERVER')
  const clientId = env('NEXT_PUBLIC_DOB_OAUTH_CLIENT_ID')

  if (!accountServer || !clientId) {
    throw new Error('DOB OAuth configuration is missing required environment variables')
  }

  const worldsBaseUrl = getDobOAuthStateUrl()
  const redirectionUrl = `${window.location.origin}${window.location.pathname}`
  const url = new URL(DOB_OAUTH_AUTHORIZE_PATH, accountServer)

  const state = JSON.stringify({
    viewToggles: ['verifyage'],
    state: worldsBaseUrl,
    scopes: [DOB_OAUTH_STATE_SCOPE],
    requireAuthCode: true,
    redirectionUrl,
    initView: DOB_OAUTH_FORCE_VIEW,
    flow: 'infinity',
    clientId,
    authorities: DOB_OAUTH_AUTHORITIES,
  })

  const query = [
    `hl=${encodeURIComponent(applyUnderline(locale))}`,
    `client_id=${encodeURIComponent(clientId)}`,
    'immediate=FALSE',
    'response_type=code',
    `scope=${encodeURIComponent(DOB_OAUTH_SCOPE)}`,
    `redirection_url=${redirectionUrl}`,
    `authorities=${DOB_OAUTH_AUTHORITIES}`,
    `state=${encodeURIComponent(state).replace(/%2B/g, '+')}`,
    `force_view=${encodeURIComponent(DOB_OAUTH_FORCE_VIEW)}`,
  ].join('&')

  url.search = query

  return url.toString()
}

export async function buildLogoutBaseUrl(): Promise<string> {
  const locale = applyUnderline(await getUserLocale())
  const domain = getCreateDomain()
  return `https://${domain}${API_ENPOINT_LOGOUT}?hl=${locale}`
}

export async function getLoginUrl({ callback, loginParams, vodafoneURLPostfix }: LoginParams) {
  const callbackParams = loginParams ? { ...loginParams } : undefined
  delete callbackParams?.authorities
  callback =
    callback && typeof callback === 'string' ? callback : getLocationHrefUrl(callbackParams)

  const loginUrl = new URL(await buildLoginBaseUrl())

  loginUrl.searchParams.set('callback', callback)

  // [CONNECT-5442] Add all extra login parameters (e.g., authorities for mobile app)
  if (loginParams) {
    Object.entries(loginParams).forEach(([key, value]) => {
      loginUrl.searchParams.set(key, value)
    })
  }

  if (vodafoneURLPostfix) {
    const postfixArray = vodafoneURLPostfix.split(',')
    const pathname = window.location.pathname
    const isVodafoneEvent = postfixArray.some((postfix) => pathname.includes('/' + postfix))

    if (isVodafoneEvent) {
      loginUrl.searchParams.set('relevance', 'vodafone')
      return loginUrl.href
    }
  }

  return loginUrl.href
}

export async function getLogoutUrl() {
  const logoutUrl = new URL(await buildLogoutBaseUrl())
  logoutUrl.searchParams.set('callback', getReferrerUrl())
  return `${logoutUrl.href}#logout`
}

type GetViveCreatorUrlOptions = {
  redirectUrl?: string
}

export function getViveCreatorUrl({ redirectUrl }: GetViveCreatorUrlOptions = {}) {
  const viveAvatarDomain = getViveAvatarDomain()
  return `https://${viveAvatarDomain}/avatar/creator?redirect_url=${encodeURIComponent(
    redirectUrl ?? getLocationHrefUrl(),
  )}`
}

// 🥷🏼 use this way to strip locale from the pathname
// 🥷🏼 ex. /user-worlds -> /user-worlds
// 🥷🏼 ex. /en-US/user-worlds -> /user-worlds
// 🥷🏼 ex. /zh-TW/profile -> /profile
// 🥷🏼 ex. /ja-JP/profile/dog?a=1 -> /profile/dog?a=1
export function stripLocale(pathname: string) {
  const localePattern = new RegExp(`^/(${routing.locales.join('|')})(?=/|$)`)
  return pathname.replace(localePattern, '')
}

// 🥷🏼 use this to get the base path of the current page
// 🥷🏼 ex. /genre/good -> /genre
export function getBasePathFromPathname(pathname: string) {
  return pathname.match(/^\/[^/]+/)?.[0] || '/'
}

// 🥷🏼 appends an already-encoded query string before any '#' fragment, without
// 🥷🏼 parsing/re-serializing the href — preserves protocol-relative hosts,
// 🥷🏼 non-http schemes, and the existing query's encoding as-is
export function appendQueryString(href: string, queryString: string): string {
  if (!href || !queryString) return href

  const hashIndex = href.indexOf('#')
  const base = hashIndex === -1 ? href : href.slice(0, hashIndex)
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex)
  const separator = !base.includes('?') ? '?' : /[?&]$/.test(base) ? '' : '&'
  return `${base}${separator}${queryString}${hash}`
}

export function withFull3d(href: string): string {
  const hasFull3d = new RegExp(`[?&]${IFRAME_SEARCH_PARAM_KEYS.FULL3D}(=|&|$)`).test(
    href.split('#')[0],
  )

  return hasFull3d ? href : appendQueryString(href, `${IFRAME_SEARCH_PARAM_KEYS.FULL3D}=`)
}
